import {
  ChevronRightIcon,
  CloudIcon,
  FileIcon,
  FolderIcon,
} from 'lucide-react';
import { type JSX, type ReactNode } from 'react';

import { Button } from '@/components/ui/button.js';
import { FilePickerRow } from '@/components/ui/file-picker-row.js';
import {
  nameWithMark,
  RowMark,
  rowMarkClass,
} from '@/components/ui/row-mark.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import type { WorkspaceSharing } from '@/features/workspace/workspace-access.js';
import {
  connectionOriginHint,
  connectionOriginLabel,
} from '@/integration/components/connectors/connection-origin.js';
import { cn } from '@/lib/utils.js';
import type {
  SharePointDrive,
  SharePointItem,
  SharePointSearchHit,
  SharePointSite,
} from '@/lib/api/sharepoint.js';

import {
  PickerBreadcrumb,
  type BrowseCrumb,
} from '@/features/microsoft/picker-chrome.js';

export type { BrowseCrumb };

import { browseErrorText } from './browse-error.js';
import type { BrowseList } from './browse-levels.js';
import type { SharePointConnectionChoice } from './connection-choice.js';
import { KNOWLEDGE_MARK_LABEL } from './knowledge-mark.js';
import { sharePointPlace } from './sharepoint-place.js';
import {
  searchHitRow,
  type SharePointFileSelection,
  type SharePointRow,
} from './sharepoint-row.js';

/**
 * The rendering half of the SharePoint tree: one level as a list, the results of a file search in the same
 * shape, plus the breadcrumbs above them. Shared by the knowledge picker's dialog, the composer's
 * Add-artifacts dialog and the Artifacts section's SharePoint tab, so all three offer the same navigation,
 * the same paging control and the same explanation when a level fails. The surfaces differ only in what a
 * row DOES — register it, attach it, pick it — which is what `renderItemAction` and `fileSelection` are
 * for. What a row IS goes through `knowledgeMark` instead, and every surface draws it identically.
 */

/**
 * What a search that found nothing says. One string, because two surfaces say it: a tree whose own search
 * came back empty, and the fan across connections — which says it once for the whole search rather than
 * once per connection.
 */
export const SEARCH_NOTHING_MATCHED =
  'Nothing in SharePoint matched this search.';

/**
 * The line above a list of hits. It replaces the breadcrumbs while a search is on screen: the path
 * describes one folder, and results come from everywhere the search reached, so leaving the path up would
 * claim these files are in the folder the user was standing in.
 */
export function SharePointSearchNote(): JSX.Element {
  return (
    <p className="text-sm text-muted-foreground">
      Results from across SharePoint, not from one folder.
    </p>
  );
}

/**
 * The path, drawn by the shared picker chrome. It is re-exported under its SharePoint name because
 * three SharePoint surfaces reach for it here; the component itself belongs to both connectors.
 */
export function SharePointBreadcrumb({
  crumbs,
}: {
  crumbs: readonly BrowseCrumb[];
}): JSX.Element {
  return <PickerBreadcrumb crumbs={crumbs} label="SharePoint path" />;
}

/**
 * Loading / error / empty placeholder for a level, or `null` when there is data to render. A plain
 * function (not a component) so a caller can branch on the return value — `if (placeholder) return
 * placeholder` — and otherwise fall through to the list.
 */
function listPlaceholder(args: {
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  isEmpty: boolean;
  emptyText: string;
}): JSX.Element | null {
  if (args.isLoading) return <Skeleton className="h-10 w-full rounded-md" />;
  if (args.isError) {
    return (
      <p className="px-2 py-3 text-sm text-destructive">
        {browseErrorText(args.error)}
      </p>
    );
  }
  if (args.isEmpty)
    return (
      <p className="px-2 py-3 text-sm text-muted-foreground">
        {args.emptyText}
      </p>
    );
  return null;
}

/**
 * The tail control of a paged level. It shows on `hasMore` alone: the page that came back may have been
 * empty, and that says nothing about whether the level is finished.
 */
function ShowMore({ list }: { list: BrowseList<unknown> }): JSX.Element | null {
  if (!list.hasMore) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="w-full"
      disabled={list.isLoadingMore}
      onClick={list.loadMore}
    >
      {list.isLoadingMore ? 'Loading…' : 'Show more'}
    </Button>
  );
}

/**
 * A row that opens the next level down, with an optional secondary action beside it — used, for
 * instance, by the add-place dialog to offer "Select" next to "Open" at a level (a site or a
 * library) that has no drill-in-only meaning of its own. Left off, this renders exactly as it
 * always has: a single button, the whole row.
 */
function DrillInRow({
  name,
  note,
  onOpen,
  action,
}: {
  name: string;
  /** A sentence under the name. See {@link RowNote}. */
  note?: string;
  onOpen: () => void;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-start gap-2 rounded-md text-sm transition-colors hover:bg-muted">
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 items-start gap-2 px-2 py-2 text-left"
      >
        <FolderIcon
          className="mt-0.5 size-4 shrink-0 text-muted-foreground"
          aria-hidden
        />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate">{name}</span>
          <RowNote note={note} />
        </span>
      </button>
      {action}
    </div>
  );
}

/**
 * A sentence under a row's name, or nothing.
 *
 * It exists for the one thing a row cannot say in the action column: WHY there is nothing to press.
 * A disabled button with its reason in a `title` is not an answer — a title never appears on a touch
 * screen and appears on a pointer only if somebody thinks to hover a control that already looks
 * dead, which is how "The entire OneDrive" came to be reported as simply unavailable.
 */
function RowNote({ note }: { note?: string }): JSX.Element | null {
  if (!note) return null;
  return <span className="text-xs text-muted-foreground">{note}</span>;
}

/**
 * The SharePoints the caller can browse here — the level above the sites, and the only one that is not
 * paged: the list comes from the workspace's presence read, which holds at most the shared connection
 * and the caller's own.
 *
 * Each row says who else reaches it, because that is what the choice actually decides: everything behind
 * a workspace connection is already shared, while a source taken out of a personal one becomes readable
 * by every member the moment it is registered.
 */
export function SharePointConnectionList({
  connections,
  sharing,
  onPick,
}: {
  connections: readonly SharePointConnectionChoice[];
  /**
   * The workspace on screen, because a connection's mark depends on it: the travelling lane is worth
   * naming only where the other one also exists. Required rather than defaulted, so a new surface
   * decides it instead of inheriting a guess.
   */
  sharing: WorkspaceSharing;
  onPick: (connection: SharePointConnectionChoice) => void;
}): JSX.Element {
  if (connections.length === 0) {
    return (
      <p className="px-2 py-3 text-sm text-muted-foreground">
        There is no SharePoint to browse. Connect one from the Add source menu,
        then come back and pick a folder.
      </p>
    );
  }
  return (
    <>
      {connections.map((connection) => {
        const origin = connectionOriginLabel(connection.scope, sharing);
        const originHint = connectionOriginHint(connection.scope, sharing);
        return (
          <button
            key={connection.connectionId}
            type="button"
            onClick={() => onPick(connection)}
            className="flex items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20"
          >
            <CloudIcon className="size-4 text-muted-foreground" aria-hidden />
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <span className="truncate">{connection.label}</span>
              {origin !== null && (
                <RowMark label={origin} title={originHint ?? undefined} />
              )}
            </span>
            <ChevronRightIcon
              className="size-4 text-muted-foreground"
              aria-hidden
            />
          </button>
        );
      })}
    </>
  );
}

/** The sites the workspace's SharePoint connection can see. */
export function SharePointSiteList({
  list,
  onPick,
  renderAction,
  renderNote,
}: {
  list: BrowseList<SharePointSite>;
  onPick: (site: SharePointSite) => void;
  /**
   * A secondary action beside "Open", such as the add-place dialog's "Select" — a site is a place
   * on its own, not only a level to drill through. Left off, a row is the drill-in button alone,
   * as every existing browse surface still wants.
   */
  renderAction?: (site: SharePointSite) => ReactNode;
  /** A sentence under a row's name — the add flows use it to say why a row cannot be added. */
  renderNote?: (site: SharePointSite) => string | undefined;
}): JSX.Element {
  const placeholder = listPlaceholder({
    isLoading: list.isLoading,
    isError: list.isError,
    error: list.error,
    isEmpty: list.rows.length === 0,
    emptyText: 'No sites found.',
  });
  if (placeholder) return placeholder;
  return (
    <>
      {list.rows.map((site) => (
        <DrillInRow
          key={site.siteId}
          name={site.name}
          {...(renderNote?.(site) ? { note: renderNote(site) } : {})}
          onOpen={() => onPick(site)}
          action={renderAction?.(site)}
        />
      ))}
      <ShowMore list={list} />
    </>
  );
}

/** A site's document libraries. */
export function SharePointDriveList({
  list,
  onPick,
  renderAction,
  renderNote,
}: {
  list: BrowseList<SharePointDrive>;
  onPick: (drive: SharePointDrive) => void;
  /** A secondary action beside "Open". See {@link SharePointSiteList}'s own `renderAction`. */
  renderAction?: (drive: SharePointDrive) => ReactNode;
  /** A sentence under a row's name — see {@link SharePointSiteList}. */
  renderNote?: (drive: SharePointDrive) => string | undefined;
}): JSX.Element {
  const placeholder = listPlaceholder({
    isLoading: list.isLoading,
    isError: list.isError,
    error: list.error,
    isEmpty: list.rows.length === 0,
    emptyText: 'No document libraries in this site.',
  });
  if (placeholder) return placeholder;
  return (
    <>
      {list.rows.map((drive) => (
        <DrillInRow
          key={drive.driveId}
          name={drive.name}
          {...(renderNote?.(drive) ? { note: renderNote(drive) } : {})}
          onOpen={() => onPick(drive)}
          action={renderAction?.(drive)}
        />
      ))}
      <ShowMore list={list} />
    </>
  );
}

/**
 * How the rows of ONE level are picked, in the terms that level has: it lists a single library, so the
 * item alone identifies a row. The address a pick is actually held by is added by whoever renders the
 * level — see {@link SharePointFileSelection}, which is what the surfaces work in.
 */
export interface SharePointItemSelection {
  isSelected: (item: SharePointItem) => boolean;
  toggle: (item: SharePointItem) => void;
}

/**
 * One folder or file, as every list here draws it. Written once so the tree's levels and the search
 * results are the same row with the same click targets — a user should not be able to tell which list a
 * row came from by how it behaves.
 *
 * A row given a {@link place} or a {@link mark} becomes a labelled group. Its name is plain text here
 * rather than a control, and the buttons beside it each speak of the file alone ("Copy link to
 * budget.xlsx"), so the group's own name is the only thing that tells a screen-reader user which row they
 * are standing in — which is why what identifies the row (where it lives) and what is true of it (it is
 * already a knowledge source) both belong in that name and not only in the pixels.
 */
function ItemRow({
  item,
  place,
  mark,
  note,
  highlighted = false,
  onOpenFolder,
  onSelectFile,
  fileVerb = 'Select',
  action,
}: {
  item: SharePointItem;
  /** Where the item lives, on a list whose rows come from everywhere. Empty in the tree. */
  place?: string;
  /** A sentence under the name. See {@link RowNote}. */
  note?: string;
  /** A state this row is IN rather than something it offers — see {@link RowMark}. */
  mark?: string;
  highlighted?: boolean;
  onOpenFolder: () => void;
  /** Given only where the surface has something to do with a chosen file. */
  onSelectFile?: (() => void) | undefined;
  /** What a click on the file does, as its accessible name says it: picks it, or opens it in the viewer. */
  fileVerb?: 'Select' | 'Open';
  action?: ReactNode;
}): JSX.Element {
  const named = Boolean(place) || mark !== undefined;
  return (
    <div
      aria-current={highlighted ? 'true' : undefined}
      role={named ? 'group' : undefined}
      aria-label={
        named
          ? nameWithMark(place ? `${item.name} in ${place}` : item.name, mark)
          : undefined
      }
      className={cn(
        'flex items-start gap-2 rounded-md px-2 py-2 text-sm hover:bg-muted',
        rowMarkClass(mark !== undefined),
        highlighted && 'bg-primary/10',
      )}
    >
      {item.isFolder ? (
        <button
          type="button"
          onClick={onOpenFolder}
          className="flex min-w-0 flex-1 items-start gap-2 text-left"
          aria-label={`Open folder ${item.name}`}
        >
          <FolderIcon
            className="mt-0.5 size-4 text-muted-foreground"
            aria-hidden
          />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate">{item.name}</span>
            <RowNote note={note} />
          </span>
        </button>
      ) : onSelectFile ? (
        <button
          type="button"
          onClick={onSelectFile}
          className="flex min-w-0 flex-1 items-start gap-2 text-left"
          aria-label={`${fileVerb} ${item.name}`}
        >
          <FileIcon
            className="mt-0.5 size-4 text-muted-foreground"
            aria-hidden
          />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate">{item.name}</span>
            <RowNote note={note} />
          </span>
        </button>
      ) : (
        <span className="flex min-w-0 flex-1 items-start gap-2">
          <FileIcon
            className="mt-0.5 size-4 text-muted-foreground"
            aria-hidden
          />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate">{item.name}</span>
            <RowNote note={note} />
          </span>
        </span>
      )}
      {mark !== undefined && <RowMark label={mark} />}
      {place ? (
        <span className="max-w-[45%] shrink-0 truncate text-xs text-muted-foreground">
          {place}
        </span>
      ) : null}
      {action}
    </div>
  );
}

/**
 * The folders and files under one parent. A folder always opens the next level; a file is only
 * selectable where the surface has something to do with a selection ({@link onSelectFile}) — the knowledge
 * picker has none, so its file rows are plain text. Whatever a surface offers per row (add it to the
 * knowledge base, say what it already is) comes in through {@link renderItemAction} rather than a flag per
 * gesture.
 *
 * A surface that PICKS files ({@link fileSelection}) gets the shared picker row instead, the same one the
 * artifacts list is made of. That row is the whole row, so it leaves no place for a per-row action: a
 * surface offers either the picking or {@link renderItemAction} on a file, not both.
 *
 * {@link knowledgeMark} is neither of those and so is neither an action nor a selection: it is a state the
 * row is IN, and it reaches both kinds of row. That is exactly why it is a separate prop — carried as an
 * action it existed only on the surfaces that hang actions on rows, which is how two of the three
 * SharePoint pickers ended up not saying what was already in the knowledge base.
 */
export function SharePointItemList({
  list,
  onOpenFolder,
  onSelectFile,
  onOpenFile,
  selectedItemId = null,
  renderItemAction,
  renderNote,
  knowledgeMark,
  fileSelection,
}: {
  list: BrowseList<SharePointItem>;
  onOpenFolder: (item: SharePointItem) => void;
  onSelectFile?: (item: SharePointItem) => void;
  /** Set where a click on a file opens it in the file viewer; it takes the place of {@link onSelectFile}. */
  onOpenFile?: (item: SharePointItem) => void;
  /** The file a link pointed at, highlighted so the user can see which one it meant. */
  selectedItemId?: string | null;
  renderItemAction?: (item: SharePointItem) => ReactNode;
  /** A sentence under a row's name — see {@link SharePointSiteList}. */
  renderNote?: (item: SharePointItem) => string | undefined;
  /**
   * Whether this row is already a knowledge source. The level lists ONE library, so the surface closes
   * over the drive it is standing in — the match is on the pair `drive_id` + `item_id`, never on the item
   * id alone, which repeats across libraries.
   */
  knowledgeMark?: (item: SharePointItem) => boolean;
  fileSelection?: SharePointItemSelection;
}): JSX.Element {
  const placeholder = listPlaceholder({
    isLoading: list.isLoading,
    isError: list.isError,
    error: list.error,
    isEmpty: list.rows.length === 0,
    emptyText: 'This folder is empty.',
  });
  if (placeholder) return placeholder;
  return (
    <>
      {list.rows.map((item) => {
        const mark =
          knowledgeMark?.(item) === true ? KNOWLEDGE_MARK_LABEL : undefined;
        return fileSelection && !item.isFolder ? (
          <FilePickerRow
            key={item.itemId}
            name={item.name}
            mark={mark}
            selected={fileSelection.isSelected(item)}
            onToggle={() => fileSelection.toggle(item)}
            className="rounded-md"
          />
        ) : (
          <ItemRow
            key={item.itemId}
            item={item}
            mark={mark}
            {...(renderNote?.(item) ? { note: renderNote(item) } : {})}
            highlighted={item.itemId === selectedItemId}
            onOpenFolder={() => onOpenFolder(item)}
            onSelectFile={
              onOpenFile
                ? () => onOpenFile(item)
                : onSelectFile
                  ? () => onSelectFile(item)
                  : undefined
            }
            fileVerb={onOpenFile ? 'Open' : 'Select'}
            action={renderItemAction?.(item)}
          />
        );
      })}
      <ShowMore list={list} />
    </>
  );
}

/**
 * What a file search found, as a flat list — there is no level here, so no breadcrumbs belong above it.
 * Every row carries its own site and library, which is what lets a surface act on a hit without walking
 * anywhere first, and a folder hit still opens: it addresses itself.
 *
 * The rows are keyed on the PAIR `drive_id` + `item_id`. Searching reaches across libraries, and Graph
 * makes an `item_id` unique only inside one of them, so the item id alone is not a key — two unrelated
 * hits would collide on it. Matching names are common (19 of 150 rows on one live tenant) and mean nothing.
 *
 * That is also why every row here shows the PLACE it was found in, which the tree's levels do not: down
 * there the user is standing in one folder and the breadcrumbs say which, while a result comes from
 * anywhere the connection reaches. The place goes into the row's accessible name too, so two hits sharing
 * a name are two different rows for a screen reader and for a click, not one row seen twice.
 */
export function SharePointSearchResultList({
  list,
  connectionId,
  onOpenFolder,
  onOpenFile,
  renderItemAction,
  knowledgeMark,
  fileSelection,
}: {
  list: BrowseList<SharePointSearchHit>;
  /**
   * The connection these hits came back from, stamped onto every row: a search says where it looked and
   * the answer does not, so this is the only place that knows which SharePoint a hit lives in.
   */
  connectionId: string | null;
  onOpenFolder: (hit: SharePointSearchHit) => void;
  /** Set where a click on a file hit opens it in the file viewer. Left off, a file hit is plain text. */
  onOpenFile?: (row: SharePointRow) => void;
  renderItemAction?: (row: SharePointRow) => ReactNode;
  /**
   * Whether this hit is already a knowledge source. It is answered at the hit's OWN address, which is the
   * only address there is here: a result comes from any library the connection reaches, so the library the
   * user happens to be standing in says nothing about it.
   */
  knowledgeMark?: (row: SharePointRow) => boolean;
  fileSelection?: SharePointFileSelection;
}): JSX.Element {
  const placeholder = listPlaceholder({
    isLoading: list.isLoading,
    isError: list.isError,
    error: list.error,
    isEmpty: list.rows.length === 0,
    emptyText: SEARCH_NOTHING_MATCHED,
  });
  if (placeholder) return placeholder;
  return (
    <>
      {list.rows.map((hit) => {
        const row = searchHitRow(hit, connectionId);
        const key = `${hit.driveId} ${hit.itemId}`;
        const place = sharePointPlace(hit.webUrl);
        const mark =
          knowledgeMark?.(row) === true ? KNOWLEDGE_MARK_LABEL : undefined;
        return fileSelection && !hit.isFolder ? (
          <FilePickerRow
            key={key}
            name={hit.name}
            place={place}
            mark={mark}
            selected={fileSelection.isSelected(row)}
            onToggle={() => fileSelection.toggle(row)}
            className="rounded-md"
          />
        ) : (
          <ItemRow
            key={key}
            item={hit}
            place={place}
            mark={mark}
            onOpenFolder={() => onOpenFolder(hit)}
            onSelectFile={onOpenFile ? () => onOpenFile(row) : undefined}
            fileVerb="Open"
            action={renderItemAction?.(row)}
          />
        );
      })}
      <ShowMore list={list} />
    </>
  );
}
